import React, { useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import {
  AppBar,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutIcon from "@mui/icons-material/LogoutOutlined";
import { brand, serifFont } from "../theme";
import { signOutUser, useSession } from "../auth/AuthProvider";

const DRAWER_WIDTH = 252;

export function Brand({ subtitle }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
      <Box
        component="img"
        src="/favicon.svg"
        alt=""
        sx={{ width: 32, height: 32, borderRadius: 1 }}
      />
      <Box sx={{ lineHeight: 1 }}>
        <Typography
          sx={{ fontFamily: serifFont, fontSize: "1.45rem", lineHeight: 1 }}
        >
          KeyPortal
        </Typography>
        {subtitle && (
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            component="div"
          >
            {subtitle}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// nav: [{ section?: string, items: [{ to, label, icon, end? }] }]
export default function AppShell({ nav, subtitle }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [open, setOpen] = useState(false);
  const session = useSession();

  const drawer = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Box sx={{ px: 2.5, py: 2.5 }}>
        <Brand subtitle={subtitle} />
      </Box>
      <Box sx={{ flex: 1, overflowY: "auto" }}>
        {nav.map((group, index) => (
          <List
            key={group.section || index}
            dense
            subheader={
              group.section && (
                <ListSubheader
                  disableSticky
                  sx={{
                    bgcolor: "transparent",
                    lineHeight: "32px",
                    fontWeight: 600,
                  }}
                >
                  {group.section}
                </ListSubheader>
              )
            }
            sx={{ px: 1.5 }}
          >
            {group.items.map((item) => (
              <ListItemButton
                key={item.to}
                component={NavLink}
                to={item.to}
                end={item.end}
                onClick={() => setOpen(false)}
                sx={{
                  borderRadius: 2,
                  mb: 0.25,
                  color: "text.secondary",
                  "& .MuiListItemIcon-root": { color: "inherit", minWidth: 36 },
                  "&.active": {
                    bgcolor: brand.sand,
                    color: "text.primary",
                    fontWeight: 600,
                  },
                }}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText
                  primary={item.label}
                  slotProps={{
                    primary: {
                      sx: { fontWeight: "inherit", fontSize: "0.92rem" },
                    },
                  }}
                />
              </ListItemButton>
            ))}
          </List>
        ))}
      </Box>
      <Divider />
      <Box sx={{ p: 2 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          component="div"
          noWrap
        >
          Signed in as
        </Typography>
        <Typography variant="body2" noWrap title={session.email} sx={{ mb: 1 }}>
          {session.email}
        </Typography>
        <Button
          size="small"
          color="inherit"
          variant="outlined"
          startIcon={<LogoutIcon fontSize="small" />}
          onClick={signOutUser}
          fullWidth
          sx={{ borderColor: "divider" }}
        >
          Sign out
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {isMobile && (
        <AppBar
          position="fixed"
          color="inherit"
          elevation={0}
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            bgcolor: "background.default",
          }}
        >
          <Toolbar>
            <IconButton
              edge="start"
              aria-label="Open menu"
              onClick={() => setOpen(true)}
              sx={{ mr: 1 }}
            >
              <MenuIcon />
            </IconButton>
            <Brand />
          </Toolbar>
        </AppBar>
      )}
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={isMobile ? open : true}
        onClose={() => setOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: isMobile ? 0 : DRAWER_WIDTH,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: DRAWER_WIDTH,
            boxSizing: "border-box",
            borderColor: "divider",
            bgcolor: isMobile ? "background.paper" : "background.default",
          },
        }}
      >
        {drawer}
      </Drawer>
      <Box component="main" sx={{ flex: 1, minWidth: 0 }}>
        {isMobile && <Toolbar />}
        <Box
          sx={{
            maxWidth: 1280,
            mx: "auto",
            px: { xs: 2, sm: 3, md: 5 },
            py: { xs: 3, md: 5 },
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
